import { AppDispatch } from '../redux/store';
import { getSustainabilityDocumentsByDisclosuresAction } from '../redux/actions/transparencyCatalogActions';
import { SustainabilityDocument } from '../redux/actions/productActions/types';
import { Product, ExternalProduct } from '../models/product/types';
import { LowEmittingTypes } from '../models/disclosures/types';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface ImpactAreaResult {
    humanHealth: boolean;
    climateHealth: boolean;
    circularEconomy: boolean;
    socialHealthEquity: boolean;
    ecosystemHealth: boolean;
}

type ProductWithLowEmitting = (Product | ExternalProduct) & {
    lowEmittingTypes?: LowEmittingTypes;
    [key: string]: unknown;
};

interface ApiResponse {
    payload?: unknown[] | { data?: unknown[] };
    data?: unknown[];
}

// ============================================================================
// Constants
// ============================================================================

const DISCLOSURE_TYPES = {
    EPD: 'epd',
    HPD: 'hpd',
    DECLARE: 'declare',
    C2C: 'c2c',
    C2C_MHC: 'c2cMHC',
    LPC: 'lpc',
} as const;

const LOW_EMITTING_DISCLOSURE_TYPE_MAP: Readonly<Record<keyof LowEmittingTypes, string>> = {
    hasClearchem: 'clearchem',
    hasCleanAirSilver: 'cleanAirSilver',
    hasCleanAirGold: 'cleanAirGold',
    hasGreenCertified: 'greenCertified',
    hasFloorscore: 'floorscore',
    hasIndoorAdvantage: 'indoorAdvantage',
    hasIndoorAdvantageGold: 'indoorAdvantageGold',
    hasGreenguard: 'greenguard',
    hasGreenguardGold: 'greenguardGold',
    hasCdphCompliant: 'cdphCompliant'
} as const;

const IMPACT_AREA_KEYWORDS = {
    humanHealth: ['human health'],
    climateHealth: ['climate health'],
    circularEconomy: ['circular', 'circularity'],
    socialHealthEquity: ['social health', 'equity'],
    ecosystemHealth: ['ecosystem health'],
} as const;

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Creates an empty ImpactAreaResult with all fields set to false
 */
const createEmptyImpactAreaResult = (): ImpactAreaResult => ({
    humanHealth: false,
    climateHealth: false,
    circularEconomy: false,
    socialHealthEquity: false,
    ecosystemHealth: false,
});

// ============================================================================
// Disclosure Collection Helpers
// ============================================================================

/**
 * Adds disclosures from the product's disclosures array to the set
 */
const addDisclosuresFromArray = (
    product: Product | ExternalProduct,
    disclosures: Set<string>,
): void => {
    if (!Array.isArray(product.disclosures)) {
        return;
    }
    
    product.disclosures.forEach((disclosure: string) => {
        if (typeof disclosure === 'string' && disclosure.trim().length > 0) {
            disclosures.add(disclosure);
        }
    });
};

/**
 * Collects all unique disclosure types from an array of products
 */
export const collectDisclosuresFromProducts = (
    products: Array<Product | ExternalProduct>,
): Set<string> => {
    const allDisclosures = new Set<string>();
    products.forEach((product) => {
        addDisclosuresFromArray(product, allDisclosures);
    });
    return allDisclosures;
};

/**
 * Extracts documents array from API response, handling various response structures
 */
const extractDocumentsFromResponse = (response: unknown): SustainabilityDocument[] => {
    if (!response) {
        return [];
    }

    // Check if response itself is an array
    if (Array.isArray(response)) {
        return response.filter((item): item is SustainabilityDocument => 
            item !== null && typeof item === 'object' && 'id' in item
        );
    }

    // Check if response is an object
    if (typeof response !== 'object') {
        return [];
    }

    const apiResponse = response as ApiResponse;

    // Check payload first
    if (apiResponse.payload) {
        if (Array.isArray(apiResponse.payload)) {
            return apiResponse.payload.filter((item): item is SustainabilityDocument => 
                item !== null && typeof item === 'object' && 'id' in item
            );
        }
        if (apiResponse.payload && typeof apiResponse.payload === 'object' && 'data' in apiResponse.payload) {
            const payloadData = (apiResponse.payload as { data?: unknown }).data;
            if (Array.isArray(payloadData)) {
                return payloadData.filter((item): item is SustainabilityDocument => 
                    item !== null && typeof item === 'object' && 'id' in item
                );
            }
        }
    }

    // Check data property
    if (Array.isArray(apiResponse.data)) {
        return apiResponse.data.filter((item): item is SustainabilityDocument => 
            item !== null && typeof item === 'object' && 'id' in item
        );
    }

    return [];
};

/**
 * Adds a document to the map with both original and lowercase key for case-insensitive lookup
 */
const addDocumentToMap = (
    map: Map<string, SustainabilityDocument[]>,
    doc: SustainabilityDocument & { disclosureType?: string },
): void => {
    const disclosureType = doc?.disclosureType;
    if (!disclosureType || typeof disclosureType !== 'string') {
        return;
    }

    const lowerType = disclosureType.toLowerCase();

    // Add with original case
    const existingDocs = map.get(disclosureType) || [];
    existingDocs.push(doc);
    map.set(disclosureType, existingDocs);

    // Add with lowercase if different for case-insensitive lookup
    if (lowerType !== disclosureType) {
        const existingLowerDocs = map.get(lowerType) || [];
        existingLowerDocs.push(doc);
        map.set(lowerType, existingLowerDocs);
    }
};

/**
 * Fetches sustainability documents for given disclosures and builds a map
 */
export const fetchDisclosureImpactAreaMap = async (
    dispatch: AppDispatch,
    disclosures: Set<string>,
): Promise<Map<string, SustainabilityDocument[]>> => {
    const disclosureImpactAreaMap = new Map<string, SustainabilityDocument[]>();

    if (disclosures.size === 0) {
        return disclosureImpactAreaMap;
    }

    try {
        const uniqueDisclosures = Array.from(disclosures);

        const response = await dispatch(
            getSustainabilityDocumentsByDisclosuresAction(uniqueDisclosures),
        );

        const docs = extractDocumentsFromResponse(response);

        // Build map of disclosure type to documents
        docs.forEach((doc) => {
            addDocumentToMap(disclosureImpactAreaMap, doc);
        });
    } catch (error) {
        console.error('Error fetching disclosure impact areas:', error);
    }

    return disclosureImpactAreaMap;
};

/**
 * Finds documents for a disclosure type (case-insensitive)
 * Uses direct lookup first, then falls back to case-insensitive search
 */
const findDocumentsForDisclosure = (
    disclosureType: string,
    disclosureImpactAreaMap: Map<string, SustainabilityDocument[]>,
): SustainabilityDocument[] => {

    if (!disclosureType) {
        return [];
    }

    // Try direct lookup first (most common case)
    const directMatch = disclosureImpactAreaMap.get(disclosureType);
    if (directMatch && directMatch.length > 0) {
        return directMatch;
    }

    // Try lowercase lookup (we store both original and lowercase keys)
    const lowerType = disclosureType.toLowerCase();
    const lowerMatch = disclosureImpactAreaMap.get(lowerType);
    if (lowerMatch && lowerMatch.length > 0) {
        return lowerMatch;
    }

    // Fallback: case-insensitive search (should rarely be needed)
    for (const [key, value] of disclosureImpactAreaMap.entries()) {
        if (key.toLowerCase() === lowerType && value.length > 0) {
            return value;
        }
    }

    return [];
};

/**
 * Updates impact area result based on area title
 */
const updateImpactAreaFromTitle = (
    result: ImpactAreaResult,
    title: string,
): void => {
    if (!title || typeof title !== 'string') {
        return;
    }

    const lowerTitle = title.toLowerCase();

    // Check each impact area's keywords
    if (IMPACT_AREA_KEYWORDS.humanHealth.some(keyword => lowerTitle.includes(keyword))) {
        result.humanHealth = true;
    }
    if (IMPACT_AREA_KEYWORDS.climateHealth.some(keyword => lowerTitle.includes(keyword))) {
        result.climateHealth = true;
    }
    if (IMPACT_AREA_KEYWORDS.circularEconomy.some(keyword => lowerTitle.includes(keyword))) {
        result.circularEconomy = true;
    }
    if (IMPACT_AREA_KEYWORDS.socialHealthEquity.some(keyword => lowerTitle.includes(keyword))) {
        result.socialHealthEquity = true;
    }
    if (IMPACT_AREA_KEYWORDS.ecosystemHealth.some(keyword => lowerTitle.includes(keyword))) {
        result.ecosystemHealth = true;
    }
};

/**
 * Gets impact areas for a specific disclosure type
 */
export const getImpactAreasForDisclosure = (
    disclosureType: string,
    disclosureImpactAreaMap: Map<string, SustainabilityDocument[]>,
): ImpactAreaResult => {
    const result = createEmptyImpactAreaResult();
    const docs = findDocumentsForDisclosure(disclosureType, disclosureImpactAreaMap);

    docs.forEach((doc) => {
        const impactAreas = doc?.impactAreas;
        if (!Array.isArray(impactAreas)) {
            return;
        }

        impactAreas.forEach((area: any) => {
            if (area?.title) {
                updateImpactAreaFromTitle(result, area.title);
            }
        });
    });

    return result;
};


/**
 * Determines which low-emitting disclosures a product has
 * Checks both lowEmittingTypes object and direct properties (for backward compatibility)
 */
export const getProductLowEmittingDisclosures = (
    product: ProductWithLowEmitting,
): {
    hasClearChem: boolean;
    hasCleanAirSilver: boolean;
    hasCleanAirGold: boolean;
    hasGreenCertified: boolean;
    hasFloorScore: boolean;
    hasIndoorAdvantage: boolean;
    hasIndoorAdvantageGold: boolean;
    hasGreenguard: boolean;
    hasGreenguardGold: boolean;
    hasCdphCompliant: boolean;
} => {
    const lowEmitting = product.lowEmittingTypes || {};
    const checkDisclosure = (key: keyof LowEmittingTypes): boolean => {
        return Boolean(lowEmitting[key] || product[key]);
    };

    return {
        hasClearChem: checkDisclosure('hasClearchem'),
        hasCleanAirSilver: checkDisclosure('hasCleanAirSilver'),
        hasCleanAirGold: checkDisclosure('hasCleanAirGold'),
        hasGreenCertified: checkDisclosure('hasGreenCertified'),
        hasFloorScore: checkDisclosure('hasFloorscore'),
        hasIndoorAdvantage: checkDisclosure('hasIndoorAdvantage'),
        hasIndoorAdvantageGold: checkDisclosure('hasIndoorAdvantageGold'),
        hasGreenguard: checkDisclosure('hasGreenguard'),
        hasGreenguardGold: checkDisclosure('hasGreenguardGold'),
        hasCdphCompliant: checkDisclosure('hasCdphCompliant')
    };
};

/**
 * Checks if disclosures array contains a specific disclosure type (case-insensitive)
 * @param excludePattern - Optional pattern to exclude from matches (e.g., 'mhc' to exclude 'c2cmhc' when searching for 'c2c')
 */
const hasDisclosureInArray = (
    disclosures: string[],
    disclosureType: string,
    excludePattern?: string,
): boolean => {
    if (!Array.isArray(disclosures) || disclosures.length === 0) {
        return false;
    }

    const lowerType = disclosureType.toLowerCase();
    const lowerExclude = excludePattern?.toLowerCase();

    return disclosures.some((disclosure: string) => {
        if (!disclosure || typeof disclosure !== 'string') {
            return false;
        }

        const lowerDisclosure = disclosure.toLowerCase();
        const hasType = lowerDisclosure === lowerType || lowerDisclosure.includes(lowerType);
        const shouldExclude = lowerExclude && lowerDisclosure.includes(lowerExclude);
        
        return hasType && !shouldExclude;
    });
};

/**
 * Determines which standard disclosures a product has
 */
export const getProductDisclosures = (
    product: Product | ExternalProduct,
): {
    hasEPD: boolean;
    hasHPD: boolean;
    hasDeclare: boolean;
    hasC2C: boolean;
    hasC2CMHC: boolean;
    hasLPC: boolean;
} => {
    const disclosures = product.disclosures || [];

    const hasEPD =
        hasDisclosureInArray(disclosures, DISCLOSURE_TYPES.EPD.toLowerCase()) ||
        Boolean(product.hasEpd || product.epdID);

    const hasHPD =
        hasDisclosureInArray(disclosures, DISCLOSURE_TYPES.HPD.toLowerCase()) ||
        Boolean(product.hasHpd || product.hpdScopeResults);

    const hasDeclare =
        hasDisclosureInArray(disclosures, DISCLOSURE_TYPES.DECLARE.toLowerCase()) ||
        Boolean(product.hasDeclare || product.declareResults);

    const hasC2C =
        hasDisclosureInArray(disclosures, DISCLOSURE_TYPES.C2C.toLowerCase(), 'c2c') ||
        Boolean(product.hasC2c || product.c2cResults);

    const hasC2CMHC =
        hasDisclosureInArray(disclosures, DISCLOSURE_TYPES.C2C_MHC.toLowerCase()) ||
        hasDisclosureInArray(disclosures, 'c2c mhc verified') ||
        Boolean(product.hasC2cMHC || product.c2cMHCResults);

    const hasLPC =
        hasDisclosureInArray(disclosures, DISCLOSURE_TYPES.LPC.toLowerCase()) ||
        Boolean(product.lpc || product.lpcResults);

    return { hasEPD, hasHPD, hasDeclare, hasC2C, hasC2CMHC, hasLPC };
};


/**
 * Gets impact areas for a disclosure if the product has it, otherwise returns empty result
 */
const getImpactAreasIfHasDisclosure = (
    hasDisclosure: boolean,
    disclosureType: string,
    disclosureImpactAreaMap: Map<string, SustainabilityDocument[]>,
): ImpactAreaResult => {
    if (hasDisclosure) {
        return getImpactAreasForDisclosure(disclosureType, disclosureImpactAreaMap);
    }
    return createEmptyImpactAreaResult();
};

/**
 * Gets all impact areas for a product based on its disclosures
 */
export const getProductImpactAreas = (
    product: Product | ExternalProduct,
    disclosureImpactAreaMap: Map<string, SustainabilityDocument[]>,
): {
    epd: ImpactAreaResult;
    hpd: ImpactAreaResult;
    declare: ImpactAreaResult;
    c2c: ImpactAreaResult;
    c2cMHC: ImpactAreaResult;
    lpc: ImpactAreaResult;
    clearChem: ImpactAreaResult;
    cleanAirSilver: ImpactAreaResult;
    cleanAirGold: ImpactAreaResult;
    greenCertified: ImpactAreaResult;
    floorscore: ImpactAreaResult;
    indoorAdvantage: ImpactAreaResult;
    indoorAdvantageGold: ImpactAreaResult;
    greenguard: ImpactAreaResult;
    greenguardGold: ImpactAreaResult;
} => {
    const { hasEPD, hasHPD, hasDeclare, hasC2C, hasC2CMHC, hasLPC } =
        getProductDisclosures(product);
    const {
        hasClearChem,
        hasCleanAirSilver,
        hasCleanAirGold,
        hasGreenCertified,
        hasFloorScore,
        hasIndoorAdvantage,
        hasIndoorAdvantageGold,
        hasGreenguard,
        hasGreenguardGold,
    } = getProductLowEmittingDisclosures(product as ProductWithLowEmitting);

    return {
        epd: getImpactAreasIfHasDisclosure(hasEPD, DISCLOSURE_TYPES.EPD, disclosureImpactAreaMap),
        hpd: getImpactAreasIfHasDisclosure(hasHPD, DISCLOSURE_TYPES.HPD, disclosureImpactAreaMap),
        declare: getImpactAreasIfHasDisclosure(hasDeclare, DISCLOSURE_TYPES.DECLARE, disclosureImpactAreaMap),
        c2c: getImpactAreasIfHasDisclosure(hasC2C, DISCLOSURE_TYPES.C2C, disclosureImpactAreaMap),
        c2cMHC: getImpactAreasIfHasDisclosure(hasC2CMHC, DISCLOSURE_TYPES.C2C_MHC, disclosureImpactAreaMap),
        lpc: getImpactAreasIfHasDisclosure(hasLPC, DISCLOSURE_TYPES.LPC, disclosureImpactAreaMap),
        clearChem: getImpactAreasIfHasDisclosure(hasClearChem, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasClearchem, disclosureImpactAreaMap),
        cleanAirSilver: getImpactAreasIfHasDisclosure(hasCleanAirSilver, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasCleanAirSilver, disclosureImpactAreaMap),
        cleanAirGold: getImpactAreasIfHasDisclosure(hasCleanAirGold, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasCleanAirGold, disclosureImpactAreaMap),
        greenCertified: getImpactAreasIfHasDisclosure(hasGreenCertified, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasGreenCertified, disclosureImpactAreaMap),
        floorscore: getImpactAreasIfHasDisclosure(hasFloorScore, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasFloorscore, disclosureImpactAreaMap),
        indoorAdvantage: getImpactAreasIfHasDisclosure(hasIndoorAdvantage, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasIndoorAdvantage, disclosureImpactAreaMap),
        indoorAdvantageGold: getImpactAreasIfHasDisclosure(hasIndoorAdvantageGold, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasIndoorAdvantageGold, disclosureImpactAreaMap),
        greenguard: getImpactAreasIfHasDisclosure(hasGreenguard, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasGreenguard, disclosureImpactAreaMap),
        greenguardGold: getImpactAreasIfHasDisclosure(hasGreenguardGold, LOW_EMITTING_DISCLOSURE_TYPE_MAP.hasGreenguardGold, disclosureImpactAreaMap),
    };
};

/**
 * Converts a boolean impact area flag to CSV value
 * @returns 'TRUE' if hasImpactArea is true, empty string otherwise
 */
export const impactAreaToCSVValue = (hasImpactArea: boolean): string => {
    return hasImpactArea ? 'TRUE' : '';
};

/**
 * Converts an ImpactAreaResult into a comma-separated string of applicable impact area names.
 * Order: Human Health, Climate Health, Circular Economy, Social Health & Equity, Ecosystem Health
 * Only includes areas that are true. Returns empty string if none apply.
 */
export const impactAreasToCSVString = (areas: ImpactAreaResult): string => {
    const labels: Array<[keyof ImpactAreaResult, string]> = [
        ['humanHealth', 'Human Health'],
        ['climateHealth', 'Climate Health'],
        ['circularEconomy', 'Circular Economy'],
        ['socialHealthEquity', 'Social Health & Equity'],
        ['ecosystemHealth', 'Ecosystem Health'],
    ];
    return labels
        .filter(([key]) => areas[key])
        .map(([, label]) => label)
        .join(', ');
};

